
import { Component, Vue, Inject } from "vue-property-decorator";
import { namespace, Getter } from "vuex-class";
import TextHighlight from "vue-text-highlight";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

import AreaTreePicker from "@/components/form/AreaTreePicker.vue";
import MapLayersSelect from "./MapLayersSelect.vue";
import RegionSelect from "@/components/form/RegionSelect.vue";

import ViewsEventBus from "@/services/views/viewsEventBus";
import { ViewObject } from "@/store/views/types";
import { cameras } from "@/utils/area-tree/configurations";
import { AreaNode } from "@/types/groups/AreaNode";
import { DeviceNode } from "@/types/devices/DeviceNode";
import { AreaContentConfiguration } from "@/types/area-tree/AreaContentConfiguration";
import { devicesApi } from "@/services/api.devices.service";
import { UserPermissions, Region } from "@/store/types";
import { AreaTreeNodeType } from "@/types/area-tree/AreaTreeNodeType";
import { getLocalRegion } from "@/utils/region-utils";

const Areas = namespace("areas");
const ViewState = namespace("views");
const Regions = namespace("regions");

@Component({
	components: {
		"map-layers": MapLayersSelect,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		"text-highlight": TextHighlight,
		"area-tree-picker": AreaTreePicker,
		"region-select": RegionSelect
	}
})
export default class ViewSidebar extends Vue {
	@Inject("viewsEventBus") private eventBus!: ViewsEventBus;

	@Getter("getFeature") private getFeature: (featureNames: string[]) => boolean;
	@Getter("getPermissions") private permissions: UserPermissions;

	@Regions.Getter private hasRegionsAvailable: boolean;

	@ViewState.Getter("getSideBarPinned") private sideBarPinned: boolean;
	@ViewState.Getter("getSelectedView") private selectedView: ViewObject;
	@ViewState.Mutation private setSideBarPinned: (pinned: boolean) => void;

	@Areas.Action private fetchAreaDictionary: () => Promise<void>;

	private isRegionCardExpanded = true;
	private isCamerasCardExpanded = true;

	private selectedRegion: Region = getLocalRegion();
	private selectedTreeItem: AreaNode | DeviceNode = null;
	private selectedCamera: any = {};

	/**
	 * Gets a value indicating whether or not search term order should be taken into account when searching for
	 * devices in views.
	 */
	private get isSearchTermsInAnyOrderEnabled() {
		return this.getFeature(["Views", "SearchTermsInAnyOrder"]);
	}

	/**
	 * Gets a value indicating whether or not the current user has permisisons to switch between regions in Views.
	 */
	private get canUserSelectRegions() {
		return this.permissions.canUseRegionsInViews && this.hasRegionsAvailable;
	}

	/**
	 * Gets the content configuration to use for the area tree. Sets up cameras to be displayed for groups, and
	 * ensures the search term order feature is passed through.
	 */
	private get treeContentConfiguration(): AreaContentConfiguration[] {
		return [
			{
				...cameras,
				onSearch: async (query, regionUrl) =>
					await devicesApi.searchDevicesForGroupTree(
						query,
						[1],
						this.isSearchTermsInAnyOrderEnabled,
						regionUrl
					)
			}
		];
	}

	/**
	 * Lifecycle hook, registers any event listeners and ensures we have the area dictionary loaded.
	 */
	public async mounted() {
		await this.fetchAreaDictionary();

		this.eventBus.$on("endCamera", this.onHideCameraSelect);
	}

	/**
	 * Handler for when the user selects an item in the area tree. Possible nodes include groups and cameras.
	 */
	private onTreeItemSelected(treeItem: AreaNode | DeviceNode) {
		this.selectedTreeItem = treeItem;

		// If the current view is locked, reset the selection and do nothing.
		if (this.selectedView.locked || treeItem == null) {
			this.selectedTreeItem = null;
			return;
		}

		const { regionId: regionID } = this.selectedRegion;

		switch (treeItem.nodeType) {
			case AreaTreeNodeType.Group: {
				this.eventBus.$emit("beginGroupSelect", {
					regionID,
					groupID: treeItem.id
				});
				break;
			}
			case AreaTreeNodeType.Camera: {
				this.eventBus.$emit("beginCamera", {
					regionID,
					deviceID: (treeItem as DeviceNode).deviceId
				});
				break;
			}
			default: {
				console.error("ViewsSidebar - Unknown item type selected in tree.");
			}
		}
	}

	private onHideCameraSelect() {
		this.selectedTreeItem = null;
	}
}
